import dayjs from "dayjs";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useAccountsControllerGetAll } from "@/api/__generated__/orval/accounts";
import { useGetCurrentUser } from "@/api/__generated__/orval/auth";
import { parseJwt } from "@/helpers/jwtTools";
import { getNavigatorLanguage } from "@/helpers/lang/getNavigatorLanguage";
import { tolgee } from "@/providers/TranslationProvider";
import {
  accessTokenAtom,
  accountsAtom,
  currentAccountIdAtom,
  userAtom,
} from "@/store";
import { useAuth } from "./authentification";

export const useAppInit = () => {
  const [_, setUser] = useAtom(userAtom);
  const setAccounts = useSetAtom(accountsAtom);
  const [currentAccountId, setCurrentAccountId] = useAtom(currentAccountIdAtom);
  const accessToken = useAtomValue(accessTokenAtom);
  const parsedAccessToken = parseJwt<{ sub: string }>(accessToken);
  const { isAuthenticated } = useAuth();

  const { data: userData, isLoading: isLoadingUserData } = useGetCurrentUser({
    query: {
      queryKey: ["currentUser", parsedAccessToken?.sub],
      enabled: isAuthenticated,
    },
  });
  const currentUserData = userData?.data?.data;

  const { data: accountsData, isLoading: isLoadingAccounts } =
    useAccountsControllerGetAll(
      { page: 1, size: 100 },
      {
        query: {
          queryKey: ["accounts", parsedAccessToken?.sub],
          enabled: isAuthenticated,
        },
      }
    );

  useEffect(() => {
    if (!isLoadingUserData && userData) {
      setUser(currentUserData);

      tolgee
        .changeLanguage(
          currentUserData?.preferredLanguage ||
            tolgee.getLanguage() ||
            getNavigatorLanguage()
        )
        .catch((e) => console.error(e));
      dayjs.locale(
        currentUserData?.preferredLanguage ||
          tolgee.getLanguage() ||
          getNavigatorLanguage()
      );
    }
  }, [isLoadingUserData, setUser, userData]);

  useEffect(() => {
    const accounts = accountsData?.data?.data;

    if (!accounts?.length) {
      return;
    }

    setAccounts(accounts);

    const isCurrentAccountValid = accounts.some(
      (a) => a.id === currentAccountId
    );

    if (!currentAccountId || !isCurrentAccountValid) {
      setCurrentAccountId(accounts[0].id);
    }
  }, [accountsData, setAccounts, currentAccountId, setCurrentAccountId]);

  return { isLoadingUserData, isLoadingAccounts, currentUserData };
};
